import { IonIcon, IonLabel } from '@ionic/react';
import {
  chevronForwardOutline,
  settingsOutline,
  brushOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Modal } from '../../components/globals/Modal';
import { UpdateUserFunc, useTags } from '../../hooks/WPHooks';
import { User } from '../../model/models';
import { LanguageParameters } from './parameters/LanguageParameters';
import { useParameters } from '../../hooks/ParametersHooks';
import { NotificationsParameters } from './parameters/NotificationsParameters';
import { DirectoryParameters } from './parameters/DirectoryParameters';
import { useWizard } from '../../hooks/WizardHooks';
import { SwitchHouses } from './parameters/SwitchHouses';
import { WP_Tag } from '../../model/tags/tags';

export const Parameters: React.FC<{ user: User; update: UpdateUserFunc }> = ({
  user,
  update,
}) => {
  return (
    <Modal
      content={<ParametersModaleContent user={user} update={update} />}
      ModalUrl={'/profile/settings'}
      backUrl={window.innerWidth > 768 ? '/profile/edit' : '/profile'}
      hideCloseBtn={window.innerWidth > 768}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center text-black">
          <div className="flex w-8 h-8 mr-4 inset-4 justify-center items-center border border-transparent rounded-full text-white bg-primary ">
            <IonIcon icon={settingsOutline} />
          </div>
          <IonLabel className="font-medium text-sm ">
            <FormattedMessage id="Settings" />
          </IonLabel>
        </div>
        <IonIcon icon={chevronForwardOutline} />
      </div>
    </Modal>
  );
};

const MAX_INTERESTS = 3;

const ParametersModaleContent: React.FC<{
  user: User;
  update: UpdateUserFunc;
}> = ({ user, update }) => {
  const { updateParameter, parameters } = useParameters(user, update);
  const { isSetLoading } = useWizard();

  const { tags: hobbies } = useTags('interest');

  const [userHobbies, setUserHobbies] = useState<Array<WP_Tag>>([]);

  useEffect(() => {
    if (hobbies) {
      const currentUserHobbies: WP_Tag[] = parameters.interest
        .map((id) => {
          return hobbies.find((tag) => tag.id === id);
        })
        .filter((t) => t !== undefined) as WP_Tag[];

      setUserHobbies(currentUserHobbies);
    }
  }, [parameters.interest, hobbies]);

  return (
    <div className="rounded-t-3xl bg-white modal-profile-content py-20 px-6 md:rounded-3xl">
      <div className="profile-header center flex flex-col items-center">
        <div className="flex w-14 h-14 inset-4 justify-center items-center border border-transparent rounded-full text-white bg-primary">
          <IonIcon
            className="w-7 h-7 text-white ios hydrated"
            icon={settingsOutline}
            role="img"
          />
        </div>
        <h2 className="font-semibold text-xl mt-4 text-black">
          <FormattedMessage id="Settings" />
        </h2>
      </div>
      {user.acf.user_house && user.acf.user_house.length > 1 && (
        <>
          <div className="my-6">
            <SwitchHouses houses={user.acf.user_house} update={update} />
          </div>
          <hr />
        </>
      )}
      <div className="text-left text-sm ion-text-wrap profile-content my-6">
        <Link
          to="/profile/hobbies"
          className="py-4 w-full h-auto border-2 border-primary rounded-xl border-opacity-40 bg-white justify-between items-center flex flex-row my-3 relative"
        >
          <div className="pl-4">
            <label className="text-black cursor-pointer font-bold text-sm">
              <FormattedMessage id="Centers of interest" />
            </label>
            <p className="text-xs h-4 text-black opacity-50 cursor-pointer">
              {userHobbies.map((interest: WP_Tag, index: number) => {
                const interestLabal = interest.acf.label
                  ? interest.acf.label.find(
                      (label) => label.lang === user.acf.locale.split('_')[0]
                    )
                  : undefined;
                const interestName = interestLabal
                  ? interestLabal.value
                  : interest.name;
                if (index === MAX_INTERESTS) {
                  return (
                    <span key={index} className="text-black">
                      ...
                    </span>
                  );
                } else if (index === userHobbies.length - 1) {
                  return (
                    <span key={index} className="text-black">
                      {interestName}
                    </span>
                  );
                } else {
                  return (
                    <span key={index} className="text-black">
                      {interestName},{' '}
                    </span>
                  );
                }
              })}
            </p>
          </div>
          <div className="flex absolute w-10 h-10 right-6 justify-center items-center border border-transparent rounded-full text-white bg-primary">
            <IonIcon
              className="text-white ios hydrated h-5 w-5"
              icon={brushOutline}
              role="img"
            />
          </div>
        </Link>
      </div>
      <hr />
      <div className="text-left text-sm ion-text-wrap profile-content mt-12">
        <h3 className="font-bold text-md text-black">
          <FormattedMessage id="Notifications" />
        </h3>
        <NotificationsParameters
          user={user}
          updateParameter={updateParameter}
          parameters={parameters}
          isSetLoading={isSetLoading}
        />
      </div>
      <hr />
      <div className="text-left text-sm ion-text-wrap profile-content mt-12">
        <h3 className="font-bold text-md text-black">
          <FormattedMessage id="Profile privacy" />
        </h3>
        <DirectoryParameters
          user={user}
          parameters={parameters}
          updateParameter={updateParameter}
          isSetLoading={isSetLoading}
        />
      </div>
      <hr />
      <div className="text-left text-sm ion-text-wrap profile-content mt-12">
        <h3 className="font-bold text-md text-black">
          <FormattedMessage id="Application language" />
        </h3>
        <div className="pb-2">
          <LanguageParameters
            user={user}
            updateParameter={updateParameter}
            parameters={parameters}
            isSetLoading={isSetLoading}
          />
        </div>
      </div>
    </div>
  );
};
